@if (dashboard(); as dashboard) {
  <section class="uls dashboard-shell">
    <div class="dashboard-overview">
      <article class="dashboard-welcome">
        <h2 class="dashboard-welcome__title">
          {{ 'home.dashboard.welcomeTitle' | translate: { name: currentUser()?.givenName } }}
        </h2>
        <p class="dashboard-welcome__description">{{ 'home.dashboard.introText' | translate }}</p>
        <div class="dashboard-welcome__actions">
          @if (addEnabled()) {
            <a
              comp-button
              size="large"
              (click)="createProduct()"
              translate="general.addNewProduct"
            ></a>
          }
        </div>
      </article>

      <div class="dashboard-content-grid">
        <div class="dashboard-metrics">
          @for (metric of dashboard.metrics; track metric.key) {
            <article class="dashboard-metric card-soft ul-card">
              <div class="dashboard-metric__header">
                <h3>{{ 'home.dashboard.metrics.' + metric.key + '.title' | translate }}</h3>
                <i comp-icon size="medium" class="dashboard-metric__icon">monitoring</i>
              </div>

              <div class="dashboard-metric__value-row">
                <div class="dashboard-metric__value">
                  {{ metric.value | number }}
                </div>
                <div class="dashboard-metric__delta">
                  <span [ngClass]="metricDeltaClass(metric.tone)">
                    {{
                      'home.dashboard.metrics.' + metric.key + '.deltaText'
                        | translate: { count: metric.deltaText }
                    }}
                  </span>
                  <span
                    class="dashboard-metric__delta_since"
                    translate="home.dashboard.metrics.sinceLastMonth"
                  ></span>
                </div>
              </div>

              @if (metric.actionLink) {
                <div class="dashboard-metric__footer">
                  <a comp-button="secondary" [routerLink]="metric.actionLink">{{
                    metric.actionLabel
                  }}</a>
                </div>
              }
            </article>
          }
        </div>
      </div>
    </div>

    <article class="dashboard-table ul-card">
      <comp-card-header>
        <span headline translate="home.recentlyManagedProducts.title"></span>
        <ng-container action-items>
          <div class="prime-action-bar">
            <a comp-button="tertiary" routerLink="/products" translate="general.showAll"></a>
          </div>
        </ng-container>
      </comp-card-header>

      <div class="dashboard-table__wrapper">
        <comp-paged-table
          [dataSource]="recentlyUsedRevisionsDataSource"
          [columns]="[
            'revisionName',
            'name',
            'description',
            'revisionIntakeType',
            'progress',
            'openFindingsCount',
            'revisionDeclarationStatus',
            'lastUpdated',
          ]"
          [modelObjectProperty]="'model.product'"
          [hidePaginator]="true"
        >
          <ng-container matColumnDef="name">
            <!--
                The default sort order for products is "by name, ascending".
                See com.ul.sis.companion.webapp.api.convert.ComparatorConfiguration
                However, you have to set start="desc" here, because the "start" attribute sets the sort order
                that is applied *after* the first click.
                 -->

            <th mat-header-cell *matHeaderCellDef mat-sort-header>
              {{ 'products.columns.product' | translate }}
            </th>
            <td mat-cell *matCellDef="let revision">
              <a
                [routerLink]="['..', 'products', revision.productId]"
                class="product-name"
                (click)="$event.stopPropagation()"
              >
                <div class="click-area">
                  {{ revision.name }}
                </div>
              </a>
            </td>
          </ng-container>

          <ng-container matColumnDef="description">
            <th mat-header-cell *matHeaderCellDef mat-sort-header disableClear start="desc">
              {{ 'products.columns.description' | translate }}
            </th>
            <td mat-cell *matCellDef="let revision">
              <comp-description
                [description]="revision.description"
                [showMaxLines]="1"
              ></comp-description>
            </td>
          </ng-container>

          <ng-container matColumnDef="revisionName">
            <th mat-header-cell *matHeaderCellDef>
              {{ 'model.productRevision.properties.revision' | translate }}
            </th>
            <td mat-cell *matCellDef="let revision">
              @if (revision.id) {
                <a
                  [routerLink]="['..', 'products', revision.productId, revision.id]"
                  class="product-name"
                  (click)="$event.stopPropagation()"
                >
                  <div class="click-area">
                    {{ revision.revisionName }}
                  </div>
                </a>
              } @else {
                {{ revision.revisionName }}
              }
            </td>
          </ng-container>

          <ng-container matColumnDef="revisionIntakeType">
            <th mat-header-cell *matHeaderCellDef>
              {{ 'products.columns.latestRevisionIntakeType' | translate }}
            </th>
            <td mat-cell *matCellDef="let revision">
              @if (revision.revisionIntakeType) {
                <comp-intake-type-chip
                  [intakeType]="revision.revisionIntakeType"
                ></comp-intake-type-chip>
              }
            </td>
          </ng-container>

          <ng-container matColumnDef="progress">
            <th mat-header-cell *matHeaderCellDef>
              {{ 'products.columns.progress' | translate }}
            </th>
            <td mat-cell *matCellDef="let revision">
              <comp-progress-bar
                [done]="revision.completedRequirementsCount"
                [total]="revision.totalRequirementsCount"
              ></comp-progress-bar>
            </td>
          </ng-container>

          <ng-container matColumnDef="openFindingsCount">
            <th mat-header-cell *matHeaderCellDef>
              {{ 'products.columns.openFindingsCount' | translate }}
            </th>
            <td mat-cell *matCellDef="let revision">
              <ul-chip
                [additionalClasses]="{
                  green: revision.openFindingsCount === 0,
                  yellow: revision.openFindingsCount > 0,
                  red: revision.openFindingsCount >= 10,
                }"
                [showDot]="true"
              >
                @if (revision.openFindingsCount == 0) {
                  {{ 'general.none' | translate }}
                } @else {
                  {{
                    'products.openFindingsCount' | translate: { count: revision.openFindingsCount }
                  }}
                }
              </ul-chip>
            </td>
          </ng-container>

          <ng-container matColumnDef="revisionDeclarationStatus">
            <th mat-header-cell *matHeaderCellDef>
              {{ 'products.columns.latestRevisionDeclarationStatus' | translate }}
            </th>
            <td mat-cell *matCellDef="let revision">
              @if (revision.revisionDeclarationStatus) {
                <ul-chip
                  [additionalClasses]="{
                    neutral: revision.revisionDeclarationStatus === 'PENDING',
                    green: revision.revisionDeclarationStatus === 'VALID',
                    red: revision.revisionDeclarationStatus === 'REVOKED',
                  }"
                >
                  {{
                    'model.productRevision.declarationStatus.' + revision.revisionDeclarationStatus
                      | translate
                  }}
                </ul-chip>
              }
            </td>
          </ng-container>

          <ng-container matColumnDef="lastUpdated">
            <th mat-header-cell *matHeaderCellDef mat-sort-header disableClear>
              {{ 'products.columns.lastUpdated' | translate }}
            </th>
            <td mat-cell *matCellDef="let revision">
              {{ revision.lastUpdated | date: 'shortDate' }}
            </td>
          </ng-container>
        </comp-paged-table>
      </div>
    </article>
  </section>
} @else {
  <div class="dashboard-loading">
    <mat-spinner></mat-spinner>
  </div>
}
